import { supabaseClient } from "@/lib/supabase";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";

interface EmailConfig {
	email_signature: string | null;
	email_salutation: string | null;
}

export async function selectEmailSignatures(
	userId: string,
): Promise<EmailConfig | null> {
	// First try to get from email_configuration
	const { data: configData, error: configError } = await supabaseClient
		.from("email_configuration")
		.select("*")
		.eq("user_id", userId)
		.maybeSingle();

	if (configError) {
		throw configError;
	}

	if (configData) {
		return {
			email_signature: configData.email_signature,
			email_salutation: configData.email_salutation,
		};
	}

	// Fallback to profiles
	const { data: profileData, error: profileError } = await supabaseClient
		.from("profiles")
		.select("name")
		.eq("user_id", userId)
		.maybeSingle();

	if (profileError) {
		throw profileError;
	}

	return profileData?.name
		? {
				email_signature: `Kind regards,\n${profileData.name}`,
				email_salutation: "Dear",
			}
		: null;
}

export const createEmailSignaturesQueryKey = (userId) => [
	"email-signatures",
	userId,
];

export const selectEmailSignaturesQueryOptions = (userId: string) => {
	return queryOptions({
		queryKey: [...createEmailSignaturesQueryKey(userId)],
		queryFn: () => selectEmailSignatures(userId),
	});
};

export function useSelectEmailSignaturesQuery(userId: string) {
	return useSuspenseQuery(selectEmailSignaturesQueryOptions(userId));
}
